import { forwardError, forwardWarn } from "@/config/logging/nr";

/** Optional scalar metadata for log lines. Use only string, number, or boolean values. Forwarded to New Relic on warn and error. */
export type LogContext = Record<string, string | number | boolean>;

function isLocalMode() {
  return import.meta.env.VITE_APP_MODE === "local";
}

function isDevBuild() {
  return import.meta.env.DEV;
}

type LogOptions = {
  attributes?: LogContext;
};

type ErrorLogOptions = LogOptions & {
  message?: string;
};

type ConsoleLogMethod = keyof Pick<
  Console,
  "debug" | "info" | "warn" | "error"
>;

function writeConsole(
  level: ConsoleLogMethod,
  message: string,
  attributes?: LogContext
) {
  if (attributes) {
    console[level](message, attributes);
    return;
  }
  console[level](message);
}

function toReportedError(errorOrMessage: unknown, message?: string): Error {
  if (errorOrMessage instanceof Error) {
    if (message !== undefined) {
      return new Error(message);
    }
    return errorOrMessage;
  }
  if (typeof errorOrMessage === "string") {
    return new Error(message ?? errorOrMessage);
  }
  return new Error(message ?? "Unexpected error");
}

function consoleErrorMessage(
  errorOrMessage: unknown,
  reportedError: Error,
  message?: string
) {
  if (message !== undefined) {
    return message;
  }
  if (typeof errorOrMessage === "string") {
    return errorOrMessage;
  }
  return reportedError.message;
}

function newRelicErrorAttributes(
  errorOrMessage: unknown,
  attributes: LogContext | undefined,
  message: string | undefined
): LogContext | undefined {
  if (message === undefined) {
    return attributes;
  }

  const merged: LogContext = { ...attributes, logMessage: message };

  if (
    errorOrMessage instanceof Error &&
    errorOrMessage.message.length > 0 &&
    errorOrMessage.message !== message
  ) {
    merged.underlyingMessage = errorOrMessage.message;
  }

  return merged;
}

/**
 * App logging facade.
 *
 * - debug: local dev only (console)
 * - info: dev builds only (console)
 * - warn/error: console + New Relic when the browser agent is loaded
 */
export const log = {
  debug(message: string, options?: LogOptions) {
    if (!isLocalMode()) return;
    writeConsole("debug", message, options?.attributes);
  },

  info(message: string, options?: LogOptions) {
    if (!isDevBuild()) return;
    writeConsole("info", message, options?.attributes);
  },

  warn(message: string, options?: LogOptions) {
    writeConsole("warn", message, options?.attributes);
    forwardWarn(message, options?.attributes);
  },

  error(errorOrMessage: unknown, options?: ErrorLogOptions) {
    const reportedError = toReportedError(errorOrMessage, options?.message);
    const message = consoleErrorMessage(
      errorOrMessage,
      reportedError,
      options?.message
    );
    const nrAttributes = newRelicErrorAttributes(
      errorOrMessage,
      options?.attributes,
      options?.message
    );

    writeConsole("error", message, options?.attributes);
    forwardError(reportedError, nrAttributes);
  },
};
